import { coco } from '@cotera/api';
import type { CustomToolRenderer } from '@cotera/client/v0/chat/tool-renderers';
import { createSubAgentChatRenderer } from './sub-agent-chat-renderer';
import { createForEachToolRenderer } from './for-each-tool-renderer';
import { ChartToolRenderer } from './chart-tool-renderer';
import { ChartDashboardToolRenderer } from './chart-dashboard-tool-renderer';
import { UploadDatasetToolRenderer } from './upload-dataset-tool-renderer';
import { UploadFileToolRenderer } from './upload-file-tool-renderer';
import { createWorkflowToolRenderer } from './workflow-tool-renderer';
import { isWorkflowToolRendererKey } from './workflow-tool-extract';
import {
  FE_ACTION_LIST_TOOL_KEY,
  isFeActionToolKey,
} from '@cotera/client/v0/actions/framework';
import { FrontendActionsListRenderer } from '../chat/components/frontend-actions-list-renderer';
import { FrontendActionRenderer } from '../chat/components/frontend-action-renderer';

const {
  COCO_RENDER_CHART_TOOL_KEY,
  COCO_RENDER_CHART_DASHBOARD_TOOL_KEY,
  isAgentToolKey,
} = coco;

const AD_HOC_SUB_AGENT_KEYS = new Set([
  'coco/sub-agent',
  'coco/cotera-agent',
  coco.COCO_COTERA_WORKSPACE_TOOL_KEY,
  coco.COCO_AGENT_EDITOR_TOOL_KEY,
  coco.COCO_DOCUMENT_EDITOR_TOOL_KEY,
  coco.COCO_FOLDER_EDITOR_TOOL_KEY,
]);
function isSubAgentLikeToolKey(toolKey: string): boolean {
  // Handle both slash format (internal) and underscore format (LLM-facing)
  const normalizedKey = toolKey.replaceAll('_', '/');
  return (
    isAgentToolKey(normalizedKey) || AD_HOC_SUB_AGENT_KEYS.has(normalizedKey)
  );
}

/**
 * Create a v1 tool renderer resolver.
 */
export function createGetV1ToolRenderer(): (
  toolKey: string
) => CustomToolRenderer | null {
  const subAgentChatRenderer = createSubAgentChatRenderer();
  const forEachRenderer = createForEachToolRenderer();
  const workflowToolRenderer = createWorkflowToolRenderer();
  return (toolKey: string): CustomToolRenderer | null => {
    if (toolKey === COCO_RENDER_CHART_TOOL_KEY) {
      return ChartToolRenderer;
    }
    if (toolKey === COCO_RENDER_CHART_DASHBOARD_TOOL_KEY) {
      return ChartDashboardToolRenderer;
    }
    if (toolKey === coco.COCO_UPLOAD_DATASET_TOOL_KEY) {
      return UploadDatasetToolRenderer;
    }
    if (toolKey === coco.COCO_UPLOAD_FILE_TOOL_KEY) {
      return UploadFileToolRenderer;
    }
    if (toolKey === coco.COCO_FOR_EACH_TOOL_KEY) {
      return forEachRenderer;
    }
    if (toolKey === FE_ACTION_LIST_TOOL_KEY) {
      return FrontendActionsListRenderer;
    }
    // Action tool keys are discovered at runtime, so they are matched by
    // namespace rather than registered one by one.
    if (isFeActionToolKey(toolKey)) {
      return FrontendActionRenderer;
    }
    if (isWorkflowToolRendererKey(toolKey)) {
      return workflowToolRenderer;
    }
    if (!isSubAgentLikeToolKey(toolKey)) {
      return null;
    }
    // Use v2 chat renderer for sub-agents (collapsible readonly chat view)
    return subAgentChatRenderer;
  };
}

/**
 * Resolver for v1 tool renderers. Agent tools use dynamic keys (coco/agent-<uuid>),
 * so we match by pattern. Also matches coco/sub-agent and coco/cotera-agent.
 */
export const getV1ToolRenderer = createGetV1ToolRenderer();
